import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { cartStorage } from "../lib/api";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => cartStorage.load());
  const [isOpen, setOpen] = useState(false);

  useEffect(() => { cartStorage.save(items); }, [items]);

  const add = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.codigo === product.codigo);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], cantidad: next[idx].cantidad + qty };
        return next;
      }
      return [...prev, {
        codigo: product.codigo,
        descripcion: product.descripcion,
        categoria: product.categoria,
        unidad: product.unidad,
        imagen: product.imagen,
        precio: product.precio,          // precio con IVA (final al cliente)
        precio_sin_iva: product.precio_sin_iva,
        iva_porcentaje: product.iva_porcentaje || 0,
        cantidad: qty,
      }];
    });
  }, []);

  const remove = useCallback(
    (codigo) => setItems((prev) => prev.filter((i) => i.codigo !== codigo)),
    []
  );

  const setQty = useCallback((codigo, cantidad) => {
    if (cantidad <= 0) return remove(codigo);
    setItems((prev) => prev.map((i) => i.codigo === codigo ? { ...i, cantidad } : i));
  }, [remove]);

  const clear = useCallback(() => setItems([]), []);

  // Total = sum(precio_con_iva * cantidad) - precios ya incluyen IVA
  // Subtotal (base) y IVA se calculan por línea con la tasa real de cada producto
  const { total, base, iva, count } = useMemo(() => {
    let totalCalc = 0;
    let baseCalc = 0;
    let ivaCalc = 0;
    let countCalc = 0;
    for (const i of items) {
      const lineTotal = (i.precio || 0) * i.cantidad;
      totalCalc += lineTotal;
      countCalc += i.cantidad;
      const tasa = i.iva_porcentaje || 0;
      if (tasa > 0) {
        const lineBase = lineTotal / (1 + tasa / 100);
        baseCalc += lineBase;
        ivaCalc += lineTotal - lineBase;
      } else {
        baseCalc += lineTotal;
      }
    }
    return {
      total: Math.round(totalCalc),
      base: Math.round(baseCalc),
      iva: Math.round(ivaCalc),
      count: countCalc,
    };
  }, [items]);

  // Backwards-compat: keep `subtotal` alias = base (without IVA)
  const subtotal = base;

  const value = useMemo(
    () => ({ items, add, setQty, remove, clear, subtotal, base, iva, total, count, isOpen, setOpen }),
    [items, add, setQty, remove, clear, subtotal, base, iva, total, count, isOpen]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
